import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Users, Award, Globe, Shield, Zap, Target, CheckCircle2, MapPin, Building2, Truck } from 'lucide-react';
import { NavLink } from 'react-router-dom';

const values = [
  {
    icon: Shield,
    title: 'Reliability',
    desc: 'We deliver on our promises with 99.95% on-time performance and transparent communication.'
  },
  {
    icon: Zap,
    title: 'Innovation',
    desc: 'Leveraging cutting-edge technology to optimize every aspect of the supply chain.'
  },
  {
    icon: Users,
    title: 'Partnership',
    desc: 'Building long-term relationships based on trust, transparency, and mutual success.'
  },
  {
    icon: Target,
    title: 'Excellence',
    desc: 'Striving for perfection in every shipment, every delivery, every interaction.'
  }
];

const milestones = [
  { year: '2018', title: 'Company Founded', desc: 'Started with a vision to revolutionize CROSSDOCS operations' },
  { year: '2020', title: 'First Hub Network', desc: 'Established strategic CROSSDOCS facilities across major markets' },
  { year: '2022', title: 'Technology Platform', desc: 'Launched proprietary logistics management software' },
  { year: '2024', title: 'Nationwide Expansion', desc: 'Achieved 48-state coverage with 25+ hub locations' }
];

const team = [
  {
    name: 'Sarah Chen',
    title: 'CEO & Founder',
    image: 'assets/man-5710164_1280.jpg',
    bio: 'Former VP of Operations at FedEx, 15+ years in logistics',
    expertise: ['Supply Chain Strategy', 'Operations Management', 'Business Development']
  },
  {
    name: 'Michael Rodriguez',
    title: 'CTO',
    image: 'assets/laptop-3196481_1280.jpg',
    bio: 'Ex-Google engineer, built scalable logistics platforms',
    expertise: ['Technology Architecture', 'AI/ML Integration', 'System Optimization']
  },
  {
    name: 'Jennifer Park',
    title: 'VP of Operations',
    image: 'assets/student-849824_1280.jpg',
    bio: 'Former Amazon logistics director, expert in warehouse optimization',
    expertise: ['Process Improvement', 'Quality Control', 'Team Leadership']
  }
];

export default function About() {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>About Us — CROSSDOCS</title>
        <meta name="description" content="Learn about CROSSDOCS, our mission to revolutionize logistics, our values, and the team behind our success." />
      </Helmet>
      
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
              Our Story
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-8">
              From a simple idea to revolutionize CROSSDOCS operations, CROSSDOCS has grown into 
              a nationwide logistics powerhouse trusted by hundreds of companies.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <NavLink 
                to="/contact" 
                className="inline-flex items-center rounded-xl bg-primary-600 px-8 py-4 text-lg font-semibold text-white shadow-glow hover:bg-primary-700 hover:shadow-glow hover:scale-105 transition-all duration-300"
              >
                Get in Touch
              </NavLink>
              <NavLink 
                to="/careers" 
                className="inline-flex items-center rounded-xl px-8 py-4 text-lg font-semibold text-white ring-2 ring-white/30 hover:ring-white/50 hover:bg-white/10 transition-all duration-300"
              >
                Join Our Team
              </NavLink>
            </div>
          </motion.div>
        </div>
        
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="assets/factory-1137994_1280.jpg" 
            alt="Manufacturing Facility"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-16 lg:grid-cols-2 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Mission & Vision
              </h2>
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-primary-600 mb-3">Our Mission</h3>
                  <p className="text-lg text-slate-600">
                    To eliminate inefficiencies in logistics by providing intelligent CROSSDOCS solutions 
                    that reduce costs, improve speed, and enhance visibility across the entire supply chain.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-primary-600 mb-3">Our Vision</h3>
                  <p className="text-lg text-slate-600">
                    To become the most trusted logistics partner for businesses seeking to optimize their 
                    supply chain operations through technology-driven solutions and exceptional service.
                  </p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-premium-lg">
                <img 
                  src="assets/warehouse-8921538_1280.jpg" 
                  alt="Modern Warehouse"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/80 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h3 className="text-2xl font-bold mb-3">State-of-the-Art Facilities</h3>
                  <p className="text-lg">Modern CROSSDOCS hubs equipped with advanced automation and real-time tracking</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Company Values */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Our Core Values
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              These fundamental principles guide every decision we make and every action we take 
              in serving our customers and building our company.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center bg-white rounded-2xl p-8 shadow-premium hover:shadow-premium-lg transition-all duration-300 hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                <p className="text-slate-600">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Journey */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Our Journey
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              From startup to industry leader, here are the key milestones that have shaped 
              CROSSDOCS into the company it is today.
            </p>
          </motion.div>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-px h-full w-0.5 bg-primary-200"></div>
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`relative flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  {/* Timeline dot */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-primary-600 rounded-full border-4 border-white shadow-lg"></div>
                  
                  {/* Content */}
                  <div className={`w-5/12 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <div className="bg-white rounded-2xl p-6 shadow-premium hover:shadow-premium-lg transition-all duration-300">
                      <div className="text-2xl font-bold text-primary-600 mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{milestone.title}</h3>
                      <p className="text-slate-600">{milestone.desc}</p>
                    </div>
            </div>
                </motion.div>
          ))}
        </div>
      </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Meet Our Leadership
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Experienced professionals with deep expertise in logistics, technology, and operations, 
              dedicated to delivering exceptional results for our customers.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl overflow-hidden shadow-premium hover:shadow-premium-lg transition-all duration-300 hover:-translate-y-2"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-1">{member.name}</h3>
                  <p className="text-primary-600 font-semibold mb-3">{member.title}</p>
                  <p className="text-slate-600 mb-4">{member.bio}</p>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Expertise:</h4>
                    <ul className="space-y-1">
                      {member.expertise.map((skill) => (
                        <li key={skill} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="h-4 w-4 text-primary-500" />
                          {skill}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              CROSSDOCS by the Numbers
            </h2>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Our growth and success metrics that demonstrate our commitment to excellence 
              and the trust our customers place in us.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '6+', label: 'Years of Excellence' },
              { number: '500+', label: 'Happy Customers' },
              { number: '25+', label: 'Strategic Hubs' },
              { number: '1300+', label: 'Carrier Partners' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-primary-200 mb-2">{stat.number}</div>
                <div className="text-gray-200">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Ready to Work with Us?
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
              Join hundreds of companies that trust CROSSDOCS with their critical logistics needs. 
              Let's discuss how we can optimize your supply chain.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <NavLink 
                to="/contact" 
                className="inline-flex items-center rounded-xl bg-primary-600 px-8 py-4 text-lg font-semibold text-white shadow-premium hover:shadow-premium-lg hover:scale-105 transition-all duration-300"
              >
                Get Started Today
              </NavLink>
              <NavLink 
                to="/careers" 
                className="inline-flex items-center rounded-xl px-8 py-4 text-lg font-semibold text-primary-700 ring-2 ring-primary-200 hover:ring-primary-300 hover:bg-primary-50 transition-all duration-300"
              >
                View Open Positions
              </NavLink>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}






